import {
  Button,
  Circle,
  Icon,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
} from '@chakra-ui/react';
import { getTenantByHost } from '@pelicargo/tenants';
import { Show } from '@pelicargo/ui';
import { paymentStatusOptions, shipmentStatusOptions } from '@pelicargo/utils';
import { isEqual } from 'lodash';
import { useState } from 'react';
import { HiChevronRight } from 'react-icons/hi';
import { HiFunnel } from 'react-icons/hi2';

import { useAuth } from '../context/AuthProvider';
import { defaultRequestFilters, RequestFilters } from '../hooks/useRequestFilters';
import { AccountsFilter } from './RequestFilters/AccountsFilter';
import { AccountTypeFilter } from './RequestFilters/AccountTypeFilter';
import { CargoTypeFilter } from './RequestFilters/CargoTypeFilter';
import { ClassificationFilter } from './RequestFilters/ClassificationFilter';
import { FreighterFilter } from './RequestFilters/FreighterFilter';
import { KnownShipperFilter } from './RequestFilters/KnownShipperFilter';
import { RequestStatusFilter } from './RequestFilters/RequestStatusFilter';
import { SimpleFilter } from './RequestFilters/SimpleFilter';
import { WeightBreakFilter } from './RequestFilters/WeightBreakFilter';

type RequestFilterItemProps = {
  label: string;
  isActive: boolean;
  filterKey: keyof RequestFilters;
  clear: (key: keyof RequestFilters) => void;
  onClick: () => void;
};

const RequestFilterItem = ({ label, isActive, onClick, filterKey, clear }: RequestFilterItemProps) => {
  const clearIndividual = (event) => {
    event.stopPropagation();
    clear(filterKey);
  };

  return (
    <Stack
      direction="row"
      align="center"
      justify="space-between"
      p="2"
      borderRadius="md"
      cursor="pointer"
      transition="all 200ms ease"
      _hover={{
        bg: 'bg.canvas',
      }}
      onClick={onClick}
    >
      <Text textStyle="captionRegular">{label}</Text>
      <Stack direction="row" align="center">
        <Show if={isActive}>
          <Button size="xs" onClick={clearIndividual}>
            Clear
          </Button>
          <Circle size="1.5" bg="primary" />
        </Show>
        <Icon as={HiChevronRight} />
      </Stack>
    </Stack>
  );
};

enum FilterView {
  CargoType = 'CargoType',
  Status = 'Status',
  KnownShipper = 'KnownShipper',
  Freighter = 'Freighter',
  Accounts = 'Accounts',
  AccountType = 'AccountType',
  Classification = 'Classification',
  WeightBreak = 'WeightBreak',
  ShipmentStatus = 'ShipmentStauts',
  PaymentStatus = 'PaymentStatus',
  Tenant = 'Tenant',
}

type Props = {
  filters: RequestFilters;
  handleCheckboxFilterChange: (key: string) => (nextValue: string[]) => void;
  handleRadioFilterChange: (key: string) => (nextValue: string) => void;
  resetFilters: () => void;
  resetSingleFilter: RequestFilterItemProps['clear'];
};

export const RequestFilter = ({
  filters,
  handleCheckboxFilterChange,
  handleRadioFilterChange,
  resetFilters,
  resetSingleFilter,
}: Props) => {
  const { knownTenants } = useAuth();

  const [view, setView] = useState<FilterView | null>(null);

  const handleChangeView = (nextView: FilterView) => () => {
    setView(nextView);
  };

  const handleClose = () => {
    setView(null);
  };

  return (
    <Popover placement="auto-end" onClose={handleClose}>
      <PopoverTrigger>
        <IconButton
          aria-label="Open"
          size="sm"
          colorScheme={isEqual(filters, defaultRequestFilters) ? 'gray' : 'brand'}
          icon={<HiFunnel />}
        />
      </PopoverTrigger>
      <PopoverContent minW="400px" p="2">
        <Show if={!view}>
          <Stack spacing="2">
            <Stack direction="row" align="center" justify="space-between" px="2">
              <Text textStyle="label">Filters</Text>
              <Button size="xs" isDisabled={isEqual(filters, defaultRequestFilters)} onClick={resetFilters}>
                Clear All
              </Button>
            </Stack>
            <Stack spacing="0">
              <RequestFilterItem
                label="Accounts"
                isActive={filters.account_id.length > 0}
                onClick={handleChangeView(FilterView.Accounts)}
                filterKey="account_id"
                clear={resetSingleFilter}
              />
              <RequestFilterItem
                label="Account Type"
                isActive={filters.account_type.length > 0}
                onClick={handleChangeView(FilterView.AccountType)}
                filterKey="account_type"
                clear={resetSingleFilter}
              />
              <RequestFilterItem
                label="Request Status"
                isActive={filters.status.length > 0}
                onClick={handleChangeView(FilterView.Status)}
                filterKey="status"
                clear={resetSingleFilter}
              />
              <RequestFilterItem
                label="Cargo Type"
                isActive={filters.cargo_type.length > 0}
                onClick={handleChangeView(FilterView.CargoType)}
                filterKey="cargo_type"
                clear={resetSingleFilter}
              />
              <RequestFilterItem
                label="Freighter Requirement"
                isActive={filters.is_freighter_required !== null}
                onClick={handleChangeView(FilterView.Freighter)}
                filterKey="is_freighter_required"
                clear={resetSingleFilter}
              />
              <RequestFilterItem
                label="Known Shipper"
                isActive={filters.is_known_shipper !== null}
                onClick={handleChangeView(FilterView.KnownShipper)}
                filterKey="is_known_shipper"
                clear={resetSingleFilter}
              />
              <RequestFilterItem
                label="Classification"
                isActive={filters.request_classification !== null}
                onClick={handleChangeView(FilterView.Classification)}
                filterKey="request_classification"
                clear={resetSingleFilter}
              />
              <RequestFilterItem
                label="Weight Break"
                isActive={filters.weight_break !== null}
                onClick={handleChangeView(FilterView.WeightBreak)}
                filterKey="weight_break"
                clear={resetSingleFilter}
              />
              <RequestFilterItem
                label="Shipment Status"
                isActive={filters.shipment_status?.length > 0}
                onClick={handleChangeView(FilterView.ShipmentStatus)}
                filterKey="shipment_status"
                clear={resetSingleFilter}
              />
              <RequestFilterItem
                label="Payment Status"
                isActive={filters.payment_status?.length > 0}
                onClick={handleChangeView(FilterView.PaymentStatus)}
                filterKey="payment_status"
                clear={resetSingleFilter}
              />
              <Show if={getTenantByHost().isSystemTenant}>
                <RequestFilterItem
                  label="Tenant"
                  isActive={filters.tenant_id?.length > 0}
                  onClick={handleChangeView(FilterView.Tenant)}
                  filterKey="tenant_id"
                  clear={resetSingleFilter}
                />
              </Show>
            </Stack>
          </Stack>
        </Show>

        <Show if={view === FilterView.Accounts}>
          <AccountsFilter
            filters={filters}
            handleCheckboxFilterChange={handleCheckboxFilterChange}
            resetView={() => setView(null)}
          />
        </Show>
        <Show if={view === FilterView.AccountType}>
          <AccountTypeFilter
            filters={filters}
            handleCheckboxFilterChange={handleCheckboxFilterChange}
            resetView={() => setView(null)}
          />
        </Show>
        <Show if={view === FilterView.Status}>
          <RequestStatusFilter
            filters={filters}
            handleCheckboxFilterChange={handleCheckboxFilterChange}
            resetView={() => setView(null)}
          />
        </Show>
        <Show if={view === FilterView.CargoType}>
          <CargoTypeFilter
            filters={filters}
            handleCheckboxFilterChange={handleCheckboxFilterChange}
            resetView={() => setView(null)}
          />
        </Show>
        <Show if={view === FilterView.Freighter}>
          <FreighterFilter
            filters={filters}
            handleRadioFilterChange={handleRadioFilterChange}
            resetView={() => setView(null)}
          />
        </Show>
        <Show if={view === FilterView.KnownShipper}>
          <KnownShipperFilter
            filters={filters}
            handleRadioFilterChange={handleRadioFilterChange}
            resetView={() => setView(null)}
          />
        </Show>
        <Show if={view === FilterView.Classification}>
          <ClassificationFilter
            filters={filters}
            handleRadioFilterChange={handleRadioFilterChange}
            resetView={() => setView(null)}
          />
        </Show>
        <Show if={view === FilterView.WeightBreak}>
          <WeightBreakFilter
            filters={filters}
            handleRadioFilterChange={handleRadioFilterChange}
            resetView={() => setView(null)}
          />
        </Show>
        <Show if={view === FilterView.PaymentStatus}>
          <SimpleFilter
            filters={filters}
            handleCheckboxFilterChange={handleCheckboxFilterChange}
            resetView={() => setView(null)}
            field="payment_status"
            name="Payment Status"
            options={paymentStatusOptions}
          />
        </Show>
        <Show if={view === FilterView.ShipmentStatus}>
          <SimpleFilter
            filters={filters}
            handleCheckboxFilterChange={handleCheckboxFilterChange}
            resetView={() => setView(null)}
            field="shipment_status"
            name="Shipment Status"
            options={shipmentStatusOptions}
          />
        </Show>
        <Show if={view === FilterView.Tenant}>
          <SimpleFilter
            filters={filters}
            handleCheckboxFilterChange={handleCheckboxFilterChange}
            resetView={() => setView(null)}
            field="tenant_id"
            name="Tenant"
            options={knownTenants.map((tenantId) => ({
              label: tenantId,
              value: tenantId,
            }))}
          />
        </Show>
      </PopoverContent>
    </Popover>
  );
};
